import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RotateCcw, Sparkles, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Practice scenarios for different contexts
const defaultScenarios = [
  "Your partner says: 'You never listen to me when I'm talking about my day.'",
  "A coworker interrupts you during a meeting and takes credit for your idea.",
  "Your teenager rolls their eyes when you ask them to help with chores.",
  "A friend cancels plans at the last minute for the third time this month.",
  "Your parent criticizes how you're raising your children.",
  "Someone cuts in front of you in a long line at the store.",
  "Your boss gives you negative feedback in front of your colleagues.",
  "A family member makes a comment about your weight or appearance.",
  "Your neighbor plays loud music late at night.",
  "Someone you care about forgets an important date or event.",
];

export default function EbookPageFooter({ pageContext = 'general', pageNumber = 1, showScenario = true, showContactForm = true }) {
  const [scenario, setScenario] = useState('');
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate scenario on mount
  useEffect(() => {
    if (showScenario) {
      generateScenario();
    }
  }, [showScenario]);

  const generateScenario = async () => {
    setIsLoadingScenario(true);
    try {
      // Try generated scenario first - use correct endpoint
      const response = await fetch(`${BACKEND_URL}/api/ai/generate-scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: pageContext,
          page_number: pageNumber,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.scenario) {
          setScenario(data.scenario);
          setIsLoadingScenario(false);
          return;
        }
      }
    } catch (error) {
      console.log('Using fallback scenarios:', error);
    }
    
    // Fallback to random scenario
    const randomScenario = defaultScenarios[Math.floor(Math.random() * defaultScenarios.length)];
    setScenario(randomScenario);
    setIsLoadingScenario(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.get('name'),
          email: formData.get('email'),
          type: 'ebook_page',
          comments: formData.get('message'),
          interest: `Belonging eBook - ${pageContext}`,
        }),
      });
      
      if (!response.ok) throw new Error('Failed');
      toast.success("Thank you! We'll be in touch soon.");
      e.target.reset();
    } catch (error) {
      toast.error('Please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-12 space-y-8" data-testid="ebook-page-footer">
      {/* Practice Scenario Section */}
      {showScenario && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-900/40 via-orange-900/30 to-rose-900/40 border border-amber-500/30 p-6 shadow-xl" data-testid="practice-scenario">
          {/* Background glow */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-40 h-40 bg-amber-400 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-rose-400 rounded-full filter blur-3xl"></div>
          </div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Practice Scenario</h3>
                  <p className="text-amber-200/70 text-sm">Generated situation for OFNR practice</p>
                </div>
              </div>
              <Button
                onClick={generateScenario}
                disabled={isLoadingScenario}
                variant="outline"
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200"
              >
                <RotateCcw className={`w-4 h-4 mr-2 ${isLoadingScenario ? 'animate-spin' : ''}`} />
                New Scenario
              </Button>
            </div>
            
            <div className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-5 border border-amber-500/20">
              {isLoadingScenario ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-amber-200">Generating scenario...</span>
                </div>
              ) : (
                <p className="text-lg text-amber-100 leading-relaxed italic">
                  "{scenario}"
                </p>
              )}
            </div>
            
            <div className="mt-4 p-4 bg-emerald-900/30 rounded-lg border border-emerald-500/20">
              <p className="text-emerald-200 text-sm">
                <strong className="text-emerald-300">Reflect:</strong> What feelings and needs come up for you in this situation? 
                Use the tools above to identify and express them using OFNR (Observation, Feeling, Need, Request).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Section */}
      {showContactForm && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-600/50 shadow-xl" data-testid="contact-form">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/2 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
          </div>
          
          <div className="relative p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect with Us</h3>
                <p className="text-slate-400 text-sm">Questions? Want to join our community?</p>
              </div>
            </div>
            
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  name="name"
                  placeholder="Your name"
                  required
                  className="bg-slate-800/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Your email"
                  required
                  className="bg-slate-800/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                />
              </div>
              <Textarea
                name="message"
                placeholder="Your message or questions about compassionate communication..."
                required
                className="bg-slate-800/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 min-h-[100px]"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-3 font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message & Join The Belonging Sessions
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center text-slate-500 text-xs mt-4">
              We respect your privacy and will never share your information.
            </p>
          </div>
        </div>
      )}
      
      {/* Footer Credit */}
      <div className="text-center py-4 border-t border-slate-700/50">
        <p className="text-slate-500 text-sm">
          © 2025 The Belonging Sessions • Building Compassionate Connection
        </p>
      </div>
    </div>
  );
}
